import { ApolloQueryResult } from 'apollo-client';

import {
    getValueFromApiFormat,
    mapDateRangePresetToApiFormat,
    mapSortParamsToString,
    mapTotalStatsToApiFormat
} from '../../mappers/common/common';
import { mapRowsToRetargetingGridRows } from '../../mappers/retargeting/retargeting';
import { mapRetargetingFilterToApiFormat } from '../../mappers/retargetings';
import { getDefaultSort } from '../../service/GridHelpers/GridHelpers';
import {
    GdRetargetingConditionTypeInput,
    RetargetingsQuery,
    RetargetingsSuggestQuery
} from '../../typings/graphql-grid';
import { defaultClientInfo, GridSourceParams } from '../../typings/grid';

import { mapGridParamsToStatRequirements } from './common';

export const mapSourceParamsToRetargetingsQueryVariables = (params: GridSourceParams) => {
    const { sortParams, view, campaignsIds, groupsIds, filter } = params;

    return {
        statRequirements: mapGridParamsToStatRequirements(params),
        orderBy: sortParams ? mapSortParamsToString(sortParams) : getDefaultSort(view),
        filter: {
            ...mapRetargetingFilterToApiFormat(filter),
            typeNotIn: [GdRetargetingConditionTypeInput.INTERESTS],
            campaignIdIn: campaignsIds,
            adGroupIdIn: groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapRetargetingQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<RetargetingsQuery>,
    params: GridSourceParams
) => {
    const { client } = result.data;
    const {
        retargetings: retargetingsResult,
        features,
        info: { workCurrency, countryRegionId },
        goals
    } = client;
    const { rowset: retargetingsRows, totalCount, totalStats, cacheKey, retargetingIds } = retargetingsResult;

    const { cost, clicks, shows, ctr } = totalStats;

    return {
        data: {
            data: mapRowsToRetargetingGridRows(retargetingsRows, goals.rowset, client, params),
            total: totalCount,
            allItemsIds: retargetingIds.map(String),
            cacheKey: getValueFromApiFormat(cacheKey)
        },
        clientInfo: {
            ...defaultClientInfo,
            ...features,
            workCurrency,
            countryRegionId
        },
        totalStats: mapTotalStatsToApiFormat({
            cost,
            clicks,
            shows,
            ctr
        }),
        reqId: result.data.reqId,
        hasObjectsOverLimit: retargetingsResult.hasObjectsOverLimit
    };
};

export const mapSourceParamsToRetargetingsAndSuggestQueryVariables = (params: GridSourceParams) => {
    const { campaignsIds, groupsIds, statPeriod, sortParams, view } = params;

    let statRequirements;
    if (statPeriod) {
        const { statPreset, statFrom, statTo } = statPeriod;
        statRequirements = {
            preset: statPreset && mapDateRangePresetToApiFormat(statPreset),
            from: statFrom,
            to: statTo
        };
    }

    return {
        orderBy: sortParams ? mapSortParamsToString(sortParams) : getDefaultSort(view),
        statRequirements,
        filter: {
            campaignIdIn: campaignsIds,
            adGroupIdIn: groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapRetargetingSuggestQueryResultToSuggestQueryResponse = (
    result: ApolloQueryResult<RetargetingsSuggestQuery>
) => {
    return {
        data: result.data.client.retargetings.rowset.reduce((acc: string[], item) => {
            if (item.retargetingCondition !== null) {
                acc.push(item.retargetingCondition.name);
            }

            return acc;
        }, []),
        reqId: result.data.reqId
    };
};
